import { useMemo } from 'react';
import { Select, Badge, Group, Text } from '@mantine/core';
import { IconBriefcase } from '@tabler/icons-react';
import { useProjectsList } from '../queries/projects';
import { useProjectStore } from './project-store';
import { reconcileProjectSelection } from './reconcile-selection';
import type { ProjectRole } from '../types/api';

export function ProjectSwitcher() {
  const { data: projects = [], isLoading } = useProjectsList();
  const appKey = useProjectStore((s) => s.appKey);
  const projectId = useProjectStore((s) => s.projectId);
  const projectRole = useProjectStore((s) => s.projectRole);
  const setProject = useProjectStore((s) => s.setProject);
  const syncRole = useProjectStore((s) => s.syncRole);
  const clearProject = useProjectStore((s) => s.clearProject);

  // Off the home landing there's always an active app; scope the switcher to
  // it so cross-app projects never show up (and never get selected).
  const scopedProjects = useMemo(
    () => (appKey ? projects.filter((p) => p.app_key === appKey) : []),
    [projects, appKey],
  );

  const data = useMemo(
    () =>
      scopedProjects.map((p) => ({
        value: p.id,
        label: `${p.company_name} · ${p.name}`,
        role: p.role as ProjectRole,
      })),
    [scopedProjects],
  );

  // Reconcile the persisted selection against the loaded list during render
  // (React-19 idiom): recover from a stale/removed projectId by auto-selecting
  // the first project (FE-B3), from a stale cross-app projectId the same way
  // (or clear it if the active app has no projects), or keep the role in sync
  // after a hard reload.
  const reconcile = reconcileProjectSelection({ projectId, projectRole, projects, isLoading, appKey });
  if (reconcile?.type === 'auto-select') {
    setProject(reconcile.id, reconcile.role);
  } else if (reconcile?.type === 'sync-role') {
    syncRole(reconcile.id, reconcile.role);
  } else if (reconcile?.type === 'clear') {
    clearProject();
  }

  const currentRole = projectId
    ? (scopedProjects.find((p) => p.id === projectId)?.role ?? null)
    : null;

  if (isLoading)
    return (
      <Text size="sm" c="dimmed">
        Loading projects…
      </Text>
    );
  if (scopedProjects.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No projects yet
      </Text>
    );
  }

  return (
    <Group gap="xs" align="center" wrap="nowrap">
      <Select
        size="sm"
        placeholder="Pick a project"
        data={data}
        value={projectId}
        searchable
        leftSection={<IconBriefcase size={16} />}
        onChange={(val) => {
          if (!val) return;
          const found = scopedProjects.find((p) => p.id === val);
          setProject(val, found?.role ?? null);
        }}
        maw={360}
        w={360}
      />
      {currentRole && (
        <Badge color={roleColor(currentRole)} variant="light" size="sm">
          {currentRole}
        </Badge>
      )}
    </Group>
  );
}

function roleColor(role: ProjectRole): string {
  switch (role) {
    case 'owner':
      return 'grape';
    case 'moderator':
      return 'blue';
    case 'editor':
      return 'teal';
    case 'viewer':
    default:
      return 'gray';
  }
}
